import {useState} from 'react'
import dayjs from 'dayjs'
import {useDialogStore} from '@ariakit/react'
import {useIsMutating} from '@tanstack/react-query'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Text} from '@/components/ui/text/Text'
import {AlertTriangleIcon, CheckCircleIcon, RefreshCw05Icon} from '@/components/ui/icon/Icon'
import {useTranslation, TKey} from '@/translations/i18n'
import {useAuthStore, usePermissions} from '@/features/auth/store/store'
import {useGenerateNrAbsences} from '@/features/accesses/queries/useGenerateNrAbsences'
import {useGenerateNrAbsencesPreview} from '@/features/accesses/queries/useGenerateNrAbsencesPreview'
import {resolveGenerateNrAbsencesErrorLabel} from '@/features/accesses/utils'
import {GenerateNrAbsencesResponse} from '@/features/accesses/types'
import {MUTATION_KEYS} from '@/queryClient'
import {DateRangeFilterEnum} from '@/types'
import {formatLocaleDate} from '@/dayjs'
import {capitalize} from '@/utilities/helpers'
import {GenerateNrAbsencesPlanStep} from './GenerateNrAbsencesPlanStep'
import {
    StyledGenerateNrAbsencesCenteredState,
    StyledGenerateNrAbsencesDialogBody,
    StyledGenerateNrAbsencesDialogFooter,
    StyledGenerateNrAbsencesErrorBox,
    StyledGenerateNrAbsencesWarningBox
} from './style'

type AccountingMonth = {month: number; year: number; label: string}

/**
 * Bulk generation is an accounting operation, reserved to Admin and Amministrazione: the absences
 * it creates are already validated and land in the month's billing, so no other role gets the CTA
 * even when its access profile grants write on accesses.
 */
const ALLOWED_ROLE_NAMES = ['Admin', 'Amministrazione']

type GenerateNrAbsencesDialogProps = {
    /* The period the registry is currently filtered on, straight from the date range filter. */
    period: {startDate: string; range: DateRangeFilterEnum}
}

/**
 * The generation fills the gap between planned and done treatments of a closed accounting month,
 * so the CTA only exists when the registry is filtered on a single month strictly before the
 * current one — a year (or an ongoing month) has no meaning for the endpoint, which takes a
 * month + year pair.
 */
const resolvePastAccountingMonth = ({
    startDate,
    range
}: GenerateNrAbsencesDialogProps['period']): AccountingMonth | null => {
    if (range != DateRangeFilterEnum.Enum.month) {
        return null
    }
    const monthStart = dayjs(startDate).startOf('month')
    if (!monthStart.isValid() || !monthStart.isBefore(dayjs().startOf('month'))) {
        return null
    }
    return {
        month: monthStart.month() + 1,
        year: monthStart.year(),
        label: capitalize(formatLocaleDate(monthStart.toDate(), 'MMMM YYYY'))
    }
}

export const GenerateNrAbsencesDialog = ({period}: GenerateNrAbsencesDialogProps) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const roleName = useAuthStore(state => state.user?.roleName)
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({open: isOpen, setOpen: setIsOpen})
    // Also covers a run left in flight by closing the dialog, keeping the CTA from starting a second one.
    const isMutating = !!useIsMutating({mutationKey: [MUTATION_KEYS.GENERATE_NR_ABSENCES]})
    const accountingMonth = resolvePastAccountingMonth(period)

    if (!roleName || !ALLOWED_ROLE_NAMES.includes(roleName) || !permissions?.accesses?.write || !accountingMonth) {
        return null
    }

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    <Button variant="secondary" size="sm" disabled={isMutating} onClick={() => setIsOpen(true)}>
                        {isMutating ? <Spinner /> : <RefreshCw05Icon />}
                        {t('accesses:generate_nr_absences')}
                    </Button>
                }
            />
            {isOpen && (
                <GenerateNrAbsencesDialogShell
                    accountingMonth={accountingMonth}
                    onClose={() => dialogStore.setOpen(false)}
                />
            )}
        </DialogProvider>
    )
}

const GenerateNrAbsencesDialogShell = ({
    accountingMonth,
    onClose
}: {
    accountingMonth: AccountingMonth
    onClose: () => void
}) => {
    const {t} = useTranslation()
    const [result, setResult] = useState<GenerateNrAbsencesResponse | null>(null)

    // Opening the dialog is what asks for the plan: the query runs on mount and, unlike the
    // confirmation, needs no user action. Its data also survives the confirmation request, so the
    // user keeps seeing what is being generated while it runs.
    const previewQuery = useGenerateNrAbsencesPreview({month: accountingMonth.month, year: accountingMonth.year})
    const mutation = useGenerateNrAbsences({onSuccess: data => setResult(data)})

    const plan = previewQuery.data ?? null
    const isPreviewPending = previewQuery.isFetching
    const isGenerating = mutation.isPending
    const errorLabel: TKey | null = mutation.isError
        ? resolveGenerateNrAbsencesErrorLabel(mutation.error)
        : previewQuery.isError
          ? resolveGenerateNrAbsencesErrorLabel(previewQuery.error)
          : null
    const totalToCreate = plan?.projects.reduce((total, project) => total + project.toCreate, 0) ?? 0
    const totalMissingCapacity = plan?.projects.reduce((total, project) => total + project.missingCapacity, 0) ?? 0

    const renderBody = () => {
        if (result) {
            return (
                <StyledGenerateNrAbsencesCenteredState>
                    <CheckCircleIcon size={32} />
                    <Text size="textMd" weight={500} palette="neutral" shade="950">
                        {t('accesses:generate_nr_absences_success_title', {count: result.createdAbsences})}
                    </Text>
                </StyledGenerateNrAbsencesCenteredState>
            )
        }

        if (isPreviewPending) {
            return (
                <StyledGenerateNrAbsencesCenteredState>
                    <Spinner size={32} />
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('accesses:generate_nr_absences_loading_preview')}
                    </Text>
                </StyledGenerateNrAbsencesCenteredState>
            )
        }

        if (!plan) {
            return (
                <StyledGenerateNrAbsencesErrorBox>
                    <AlertTriangleIcon size={16} />
                    {t(errorLabel ?? 'errors:default')}
                </StyledGenerateNrAbsencesErrorBox>
            )
        }

        // Nothing to create can mean two very different things: either the month is already
        // covered, or there is a deficit the backend could not place on any day of the month.
        if (totalToCreate == 0) {
            return totalMissingCapacity > 0 ? (
                <StyledGenerateNrAbsencesWarningBox>
                    <AlertTriangleIcon size={16} />
                    {t('accesses:generate_nr_absences_missing_capacity', {count: totalMissingCapacity})}
                </StyledGenerateNrAbsencesWarningBox>
            ) : (
                <StyledGenerateNrAbsencesCenteredState>
                    <CheckCircleIcon size={32} />
                    <Text size="textMd" weight={500} palette="neutral" shade="950">
                        {t('accesses:generate_nr_absences_empty_title')}
                    </Text>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('accesses:generate_nr_absences_empty_description')}
                    </Text>
                </StyledGenerateNrAbsencesCenteredState>
            )
        }

        return (
            <>
                <GenerateNrAbsencesPlanStep
                    plan={plan}
                    totalToCreate={totalToCreate}
                    totalMissingCapacity={totalMissingCapacity}
                />
                {errorLabel && (
                    <StyledGenerateNrAbsencesErrorBox>
                        <AlertTriangleIcon size={16} />
                        {t(errorLabel)}
                    </StyledGenerateNrAbsencesErrorBox>
                )}
            </>
        )
    }

    const renderFooter = () => {
        if (result) {
            return (
                <Button variant="primaryNeutral" onClick={onClose}>
                    {t('commons:close')}
                </Button>
            )
        }

        if (isPreviewPending) {
            return (
                <Button variant="secondary" onClick={onClose}>
                    {t('commons:cancel')}
                </Button>
            )
        }

        if (!plan) {
            return (
                <>
                    <Button variant="secondary" onClick={onClose}>
                        {t('commons:close')}
                    </Button>
                    <Button variant="primaryNeutral" onClick={() => void previewQuery.refetch()}>
                        {t('commons:retry')}
                    </Button>
                </>
            )
        }

        if (totalToCreate == 0) {
            return (
                <Button variant="primaryNeutral" onClick={onClose}>
                    {t('commons:close')}
                </Button>
            )
        }

        return (
            <>
                <Button variant="secondary" onClick={onClose} disabled={isGenerating}>
                    {t('commons:cancel')}
                </Button>
                <Button
                    variant="primaryNeutral"
                    onClick={() =>
                        mutation.mutate({
                            month: accountingMonth.month,
                            year: accountingMonth.year,
                            dryRun: false
                        })
                    }
                    disabled={isGenerating}
                >
                    {t('accesses:generate_nr_absences_submit', {count: totalToCreate})}
                    {isGenerating && <Spinner />}
                </Button>
            </>
        )
    }

    return (
        <Dialog width="600px" hideOnInteractOutside={!isGenerating} hideOnEscape={!isGenerating}>
            <DialogHeader>
                <DialogHeading>
                    {t('accesses:generate_nr_absences_dialog_title', {period: accountingMonth.label})}
                </DialogHeading>
                <DialogDismiss disabled={isGenerating} />
            </DialogHeader>
            <StyledGenerateNrAbsencesDialogBody>{renderBody()}</StyledGenerateNrAbsencesDialogBody>
            <StyledGenerateNrAbsencesDialogFooter>{renderFooter()}</StyledGenerateNrAbsencesDialogFooter>
        </Dialog>
    )
}
